import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { CarPickDto, OptionDto } from '@impact/data';
import { Observable } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';

import { arrowRightIcon } from '../../../../icons';
import { WorkshopService } from '../../../core/workshop.service';
import { IBooking } from '../../../models/book-workshop.models';

@Component({
    selector: 'app-book-workshop-select-delivery',
    template: `
        <section class="flow-step__header">
            <h1 class="h4">{{ 'book_workshop.step_delivery' | translate }}</h1>
            <div>{{ 'book_workshop.choose_car_delivery' | translate }}</div>
        </section>
        <ul class="book-workshop-select-delivery-list">
            <div class="loader" *ngIf="loading"></div>
            <li *ngFor="let deliveryOption of deliveryOptions$ | async" class="book-workshop-select-delivery-item">
                <input
                    class="book-workshop-select-delivery-item__radio"
                    type="radio"
                    name="delivery"
                    [id]="'delivery__' + deliveryOption.id"
                    [value]="deliveryOption"
                    [(ngModel)]="selectedDeliveryOption"
                />
                <label
                    [for]="'delivery__' + deliveryOption.id"
                    class="book-workshop-select-delivery-item__content"
                    appCircleCheckmark
                    [condition]="deliveryOption.id === selectedDeliveryOption?.id"
                >
                    <div class="book-workshop-select-delivery-item__name">
                        {{ deliveryOption.name }}
                    </div>
                    <div class="book-workshop-select-delivery-item__description">
                        {{ deliveryOption.description }}
                    </div>
                    <div
                        *ngIf="deliveryOption.requiresAddress && selectedDeliveryOption && selectedDeliveryOption.id === deliveryOption.id"
                        class="book-workshop-select-delivery-item-expansion"
                        [formGroup]="form"
                    >
                        <div class="book-workshop-select-delivery-item-expansion__heading">
                            {{ 'book_workshop.address' | translate }}
                        </div>
                        <div class="book-workshop-select-delivery-item__address-container">
                            <app-input
                                class="book-workshop-select-delivery-item__address"
                                controlName="address"
                                [parentForm]="form"
                                [label]="'book_workshop.address' | translate"
                            ></app-input>

                            <app-input
                                class="book-workshop-select-delivery-item__zipCode"
                                controlName="zipCode"
                                [inputMode]="'decimal'"
                                [label]="'book_workshop.zip_code' | translate"
                                [parentForm]="form"
                            ></app-input>

                            <app-input
                                class="book-workshop-select-delivery-item__city"
                                controlName="city"
                                [parentForm]="form"
                                [label]="'book_workshop.city' | translate"
                            ></app-input>
                        </div>
                        <div class="book-workshop-select-delivery-item-expansion__heading">
                            {{ 'book_workshop.point_in_time' | translate }}
                        </div>
                        <app-input
                            class="book-workshop-select-delivery-item__time"
                            type="time"
                            controlName="time"
                            [parentForm]="form"
                            [label]="'book_workshop.point_in_time' | translate"
                        ></app-input>
                    </div>
                </label>
            </li>
        </ul>
        <div class="book-workshop-flow__bottom">
            <div class="book-workshop-flow__bottom-content">
                <app-button
                    buttonClass="book-workshop-stepper-content__continue-button button--tertiary"
                    (click)="previousStep.emit()"
                    [title]="'book_workshop.back' | translate"
                ></app-button>
                <app-button
                    buttonClass="book-workshop-stepper-content__continue-button button--primary"
                    [disabled]="!canGoToNextStep() || loading"
                    (handleClick)="onNextStepClick()"
                    [title]="'book_workshop.continue' | translate">
                    <i class="button__icon">${arrowRightIcon}</i>
                </app-button>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookWorkshopSelectDeliveryComponent implements OnInit {
    @Input() booking: IBooking | null;
    @Output() nextStep = new EventEmitter();
    @Output() previousStep = new EventEmitter();

    deliveryOptions$: Observable<OptionDto[]>;
    selectedDeliveryOption?: OptionDto;
    loading = true;
    form = this.fb.group({
        address: ['', Validators.required],
        zipCode: ['', Validators.required],
        city: [''],
        time: ['', Validators.required],
    });

    constructor(private workshopService: WorkshopService, private fb: UntypedFormBuilder) {}

    ngOnInit(): void {
        if (this.booking) {
            this.deliveryOptions$ = this.workshopService.getBookingOptions(this.booking).pipe(
                startWith([]),
                tap((bookingOptions) => {
                    this.loading = false;
                    this.selectedDeliveryOption = bookingOptions.find((option) => option.id === this.booking?.optionId);
                })
            );
        }
    }

    onNextStepClick() {
        const currentDate = new Date().toDateString();
        const time = this.form.get('time')?.value;

        const pickupData: CarPickDto = {
            address: this.form.get('address')?.value,
            time: new Date(`${currentDate} ${time}`).toISOString(),
            zipCode: this.form.get('zipCode')?.value,
        };

        this.nextStep.emit({
            optionId: this.selectedDeliveryOption?.id,
            delivery: this.selectedDeliveryOption?.name,
            pickupData,
        });
    }

    canGoToNextStep() {
        return (this.selectedDeliveryOption && !this.selectedDeliveryOption.requiresAddress) || (this.selectedDeliveryOption && this.form.valid);
    }
}
